/** Readiness tab (Phase 4 slice 1): renders GET /api/workflows/:id/readiness
 * as a per-section checklist, with a "Fix" deep link on items that carry
 * one. Read-only — no Customize-mode wiring here.
 * Spec: docs/superpowers/specs/2026-08-17-workflow-readiness-checklist-design.md */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle, HelpCircle, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import { SelfTestPanel } from './SelfTestPanel'
import type {
  ReadinessFix,
  ReadinessItem,
  ReadinessSection,
  ReadinessStatus,
  WorkflowReadinessRead,
} from '@/types/workflowConfig'

// Every id in shared/readiness-sections.json needs a title here, or its
// heading reads the raw id (the test renders them all; SA-938).
const SECTION_TITLES: Record<string, string> = {
  business_config: 'Business configuration',
  capabilities: 'Capabilities',
  personas: 'Personas & channels',
  template_health: 'Template health',
  self_test: 'Self-test',
  manifest: 'Manifest',
  mailboxes: 'Mailboxes',
  parameters: 'Parameters',
  process: 'Process',
}

// Route literals verified against frontend/src/App.tsx (Task 9 Step 1 —
// `grep -n "path=" src/App.tsx`):
//   - `/admin/business` and `/admin/integrations` match exactly.
//   - There is NO bare `/personas/:personaId` route — every callsite that
//     links to a specific persona's edit surface (WorkflowOverviewPage,
//     ToolLibrary/AdapterCard, App.tsx's own ComposerRedirect) uses
//     `/personas/:personaId/workshop`, so the persona surface follows suit.
//   - `/workflows/:workflowId` exists, and its `?tab=` query param is the
//     real deep-link mechanism (`workflowOverviewTabs.initialTabFor` reads
//     `?tab=` and prefers it over the stored tab; `versions` is a member of
//     its KNOWN_TABS allowlist) — not a guess.
// Update here if the app's routes move; the tests pin these.
const SURFACE_ROUTES: Record<
  ReadinessFix['surface'],
  (target: string | null, workflowId: string) => string
> = {
  business: () => '/admin/business',
  integrations: (target) => (target ? `/admin/integrations?adapter=${target}` : '/admin/integrations'),
  persona: (target) => (target ? `/personas/${target}/workshop` : '/personas'),
  customize: (_target, workflowId) => `/workflows/${workflowId}`,
  versions: (_target, workflowId) => `/workflows/${workflowId}?tab=versions`,
  // SA-111: `target` carries the mailbox id, but the admin page has no
  // per-row anchor today — the link lands on the page itself.
  mailboxes: () => '/admin/mailboxes',
  ai_models: () => '/admin/ai-models',
}

export function fixHref(fix: ReadinessFix, workflowId: string): string {
  return SURFACE_ROUTES[fix.surface](fix.target, workflowId)
}

const STATUS_BADGE: Record<
  ReadinessStatus,
  { variant: 'success' | 'warning' | 'secondary' | 'destructive'; label: string }
> = {
  ok: { variant: 'success', label: 'OK' },
  attention: { variant: 'warning', label: 'Attention' },
  unknown: { variant: 'secondary', label: 'Unknown' },
  blocked: { variant: 'destructive', label: 'Blocked' },
}

const STATUS_ICON: Record<ReadinessStatus, typeof CheckCircle> = {
  ok: CheckCircle,
  attention: AlertTriangle,
  unknown: HelpCircle,
  blocked: XCircle,
}

function ReadinessStatusBadge({ status }: { status: ReadinessStatus }) {
  const { variant, label } = STATUS_BADGE[status]
  const Icon = STATUS_ICON[status]
  return (
    <Badge variant={variant}>
      <Icon />
      {label}
    </Badge>
  )
}

function ReadinessItemRow({ item, workflowId }: { item: ReadinessItem; workflowId: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <ReadinessStatusBadge status={item.status} />
      <div className="min-w-0">
        <p className="text-foreground">
          <span className="font-medium">{item.label}</span>
          <span className="ml-2 text-xs text-muted-foreground">{item.detail}</span>
        </p>
        {item.fix && (
          <Link
            className="text-xs font-medium text-primary hover:underline"
            to={fixHref(item.fix, workflowId)}
          >
            Fix →
          </Link>
        )}
      </div>
    </li>
  )
}

// AC1: the Run button appears iff the template declares a self-test suite —
// derived from the `self_test.suite` item's status (`check_self_test`,
// workflow_readiness.py:358-372: `attention` = "this template ships no
// self-test suite"; `ok`/`unknown` both mean a suite IS declared).
function suiteDeclared(section: ReadinessSection): boolean {
  const suiteItem = section.items.find((item) => item.id === 'self_test.suite')
  return suiteItem ? suiteItem.status !== 'attention' : false
}

export function useWorkflowReadiness(workflowId: string | undefined) {
  return useQuery<WorkflowReadinessRead, Error>({
    queryKey: ['workflows', 'readiness', workflowId ?? ''],
    queryFn: () => apiClient.get<WorkflowReadinessRead>(`/api/workflows/${workflowId}/readiness`),
    enabled: !!workflowId,
    staleTime: 60_000,
  })
}

export function ReadinessTab({ workflowId }: { workflowId: string }) {
  const readiness = useWorkflowReadiness(workflowId)

  if (readiness.isLoading) {
    return <p className="py-4 text-sm text-muted-foreground">Checking readiness…</p>
  }
  if (readiness.isError || !readiness.data) {
    return <p className="py-4 text-sm text-destructive">Readiness could not be loaded.</p>
  }

  return (
    <div className="space-y-6">
      {readiness.data.sections.map((section) => (
        <section key={section.id} className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            {SECTION_TITLES[section.id] ?? section.id}
          </h3>
          <ul className="space-y-2">
            {section.items.map((item, idx) => (
              <ReadinessItemRow
                key={`${item.id}:${item.persona_id ?? idx}`}
                item={item}
                workflowId={workflowId}
              />
            ))}
          </ul>
          {section.id === 'self_test' && (
            <SelfTestPanel workflowId={workflowId} suiteDeclared={suiteDeclared(section)} />
          )}
        </section>
      ))}
    </div>
  )
}
